import { Controller, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  ClientProfileService,
  ReportsProfileService,
  VehicleProfileService,
} from "subModule/src/core/services";
import {
  GetReportPreviewResponse,
  GetReportsListResponse,
} from "subModule/src/services/reports/interface";
import DynamicSelect from "common/Select/Select";
import { ReactComponent as EmptyReport } from "assets/report-empty.svg";
import { GetClientsDropdownResponse } from "subModule/src/services/client/interface";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { GetVehicleResponse } from "subModule/src/services/vehicle/interface";
import StyledInput from "common/SearchField/Search";
import dayjs from "dayjs";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ReportPreviewTable } from "./ReportPreviewTable";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { ReactComponent as DownloadPDF } from "assets/download-pdf.svg";
import { ReactComponent as SalesReportIcon } from "assets/reports/report_sales.svg";
import { ReactComponent as InventoryReportIcon } from "assets/reports/report_inventory.svg";
import { ReactComponent as KpiReportIcon } from "assets/reports/report_kpis.svg";
import { ReactComponent as OthersReportIcon } from "assets/reports/report_others.svg";
import { RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import { ReactComponent as CheckBoxUnchecked } from "../../assets/Modal/Rectangle 34624559.svg";
import { ReactComponent as CheckBoxchecked } from "../../assets/Modal/checked-box.svg";
import DateRangePicker from "common/Pickers/DateRangePicker";
import { checkSubscriptions } from "Helpers/CheckSubscriptions";
interface FormData {
  [key: string]: any;
}
interface Option {
  label: string;
  value: string;
}
const steps = ["type", "templates", "units", "dateInterval"];

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [customerLoader, setCustomerLoader] = useState<boolean>(false);
  const [vehicleLoader, setVehicleLoader] = useState<boolean>(false);
  const [reportOptions, setReportOptions] = useState<Option[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<any>();
  const [filteredVehicles, setFilteredVehicles] = useState<any>();
  const [tab, setTab] = useState<number>(0);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const [IsPdfBtnEnabled, setIsPdfBtnEnabled] = useState<boolean>(false);
  const [showReportPreview, setShowReportPreview] = useState<boolean>(false);
  const [report, setReport] = useState<any>({});
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState<number>(10);
  const [reportPreviewLoader, setReportPreviewLoader] =
    useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    trigger,
    getValues,
    reset,
    resetField,
    setValue,
  } = useForm<FormData>({ defaultValues: {} });
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      name: dropdownSearchTerm,
    });
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string; code: string }) => ({
      value: item.id,
      label: item.name + " | " + item.code,
    }),
  );
  useEffect(() => {
    if (dropdownSearchTerm) setDropdownSearchTerm("");
    if (dropdownPage > 1) setDropdownPage(1);
    setTimeout(() => {
      if (currentStep === 1) {
        fetchReportsList();
        stationsRefetch();
      }
      if (
        currentStep === 2 &&
        (watch("reportType")?.value !== 2 ||
          watch("reportNumber")?.value !== 203 ||
          watch("reportNumber")?.value !== 204 ||
          watch("reportNumber")?.value !== 303 ||
          watch("reportNumber")?.value !== 205 ||
          watch("reportNumber")?.value !== 12 ||
          watch("reportNumber")?.value !== 206)
      ) {
        setTimeout(() => fetchCustomers(1, ""), 500);
        if (
          watch("reportNumber")?.value !== 4 ||
          watch("reportNumber")?.value !== 12 ||
          watch("reportNumber")?.value !== 13 ||
          watch("reportNumber")?.value !== 16 ||
          watch("reportNumber")?.value !== 17
        )
          fetchVehicles(1, "");
      }
      if (currentStep < steps?.length - 1) {
        setShowReportPreview(false);
        setReport([]);
        setSearchText("");
      }
    }, 0);
  }, [currentStep]);
  const fetchReportsList = () => {
    setLoading(true);
    ReportsProfileService.getReportsList({ type: watch("reportType")?.value })
      .then((res: GetReportsListResponse) => {
        if (res.success) {
          setReportOptions(
            Object.keys(res.data || {})?.map((key) => ({
              label: key
                ?.replace(/_/g, " ")
                ?.toLowerCase()
                ?.replace(/\b\w/g, (char) => char.toUpperCase()),
              value: (res.data as any)[key],
              key,
            })),
          );
          setLoading(false);
        } else {
          setReportOptions([]);
          enqueueSnackbar(
            <AlertMessages text={t(res.message) || "Something went wrong"} />,
            { variant: "error" },
          );
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setReportOptions([]);
        setLoading(false);
      });
  };
  const fetchCustomers = (
    currentPage: number,
    searchingText: string,
    action?: "searchIt" | "loadNext",
  ) => {
    setCustomerLoader(true);
    ClientProfileService.getClientsDropdown({
      page: currentPage,
      size: 20,
      type: [160, 170, 180],
      search: searchingText,
    })
      .then((res: GetClientsDropdownResponse) => {
        if (res.success) {
          if (action === "loadNext") {
            setFilteredCustomers((prev: any) => ({
              ...res.data,
              content: [...(prev?.content || []), ...(res.data?.content || [])],
            }));
          } else {
            setFilteredCustomers(res.data || {});
          }
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => {
        setCustomerLoader(false);
        setSearchLoading(null);
      });
  };
  const fetchVehicles = (
    currentPage: number,
    searchingText: string,
    action?: "searchIt" | "loadNext",
  ) => {
    setVehicleLoader(true);
    if (
      watch("reportNumber")?.value === 202 ||
      watch("reportNumber")?.value === 302
    ) {
      ClientProfileService.getAccounts({
        page: currentPage,
        size: 20,
        search: searchingText,
      })
        .then((res: any) => {
          if (res.success) {
            if (action === "loadNext") {
              setFilteredVehicles((prev: any) => ({
                ...res.data,
                content: [
                  ...(prev?.content || []),
                  ...(res.data?.content || []),
                ],
              }));
            } else {
              setFilteredVehicles(res.data || {});
            }
          } else {
            enqueueSnackbar(
              <AlertMessages text={res.message || "Something went wrong"} />,
              { variant: "error" },
            );
          }
        })
        .catch((err: any) => {
          console.error("===", err);
        })
        .finally(() => {
          setVehicleLoader(false);
          setSearchLoading(null);
        });
    } else {
      VehicleProfileService.getVehiclesDropdown({
        page: currentPage,
        size: 20,
        search: searchingText,
      })
        .then((res: GetVehicleResponse) => {
          if (res.success) {
            if (action === "loadNext") {
              setFilteredVehicles((prev: any) => ({
                ...res.data,
                content: [
                  ...(prev?.content || []),
                  ...(res.data?.content || []),
                ],
              }));
            } else {
              setFilteredVehicles(res.data || {});
            }
          } else {
            enqueueSnackbar(
              <AlertMessages text={res.message || "Something went wrong"} />,
              { variant: "error" },
            );
          }
        })
        .catch((err: any) => {
          console.error("===", err);
        })
        .finally(() => {
          setVehicleLoader(false);
          setSearchLoading(null);
        });
    }
  };
  const customerCheckboxes = useMemo(
    () =>
      (filteredCustomers?.content || [])?.map((row: any) => (
        <Controller
          key={row.id}
          name="clientId"
          control={control}
          defaultValue={[]}
          rules={{
            validate: (value) =>
              watch("reportNumber")?.value === 4 && value.length !== 1
                ? t("selectACustomer")
                : true,
          }}
          render={({ field }) => {
            const selectedIds = field.value as number[];
            const isChecked = selectedIds.includes(row.id);
            return (
              <CommonCheckbox
                label={`${row.name}${row.phone ? " | " + row.phone : ""}`}
                onChange={(event) => {
                  let newIds;
                  if (watch("reportNumber")?.value === 4) {
                    newIds = event.target.checked ? [row.id] : [];
                  } else {
                    newIds = event.target.checked
                      ? [...selectedIds, row.id]
                      : selectedIds.filter((id) => id !== row.id);
                  }
                  field.onChange(newIds);
                  // trigger("clientId");
                }}
                checked={isChecked}
                color={themeColors?.primary}
                icon={
                  watch("reportNumber")?.value === 4 ? (
                    <RadioButtonUnchecked width="36px" height="36px" />
                  ) : (
                    <CheckBoxUnchecked width="36px" height="36px" />
                  )
                }
                checkedIcon={
                  watch("reportNumber")?.value === 4 ? (
                    <RadioButtonChecked width="36px" height="36px" />
                  ) : (
                    <CheckBoxchecked width="36px" height="36px" />
                  )
                }
              />
            );
          }}
        />
      )),
    [filteredCustomers, control, themeColors?.primary],
  );
  const vehicleCheckboxes = useMemo(
    () =>
      (filteredVehicles?.content || [])?.map((row: any) => (
        <Controller
          key={row.id}
          name={"vehicleId"}
          control={control}
          defaultValue={[]}
          render={({ field }) => {
            const selectedIds = field.value as number[];
            const isChecked = selectedIds.includes(row.id);
            return (
              <CommonCheckbox
                label={
                  watch("reportNumber")?.value === 202 ||
                  watch("reportNumber")?.value === 302
                    ? row?.title || row.id
                    : `${row.makeTitle} ${row.modelTitle} ${
                        row.modelEngineTitle || ""
                      }${row.plateNumber ? " | " + row.plateNumber : ""}`
                }
                onChange={(event) => {
                  const newIds = event.target.checked
                    ? [...selectedIds, row.id]
                    : selectedIds.filter((id) => id !== row.id);
                  field.onChange(newIds);
                }}
                checked={isChecked}
                color={themeColors?.primary}
              />
            );
          }}
        />
      )),
    [filteredVehicles, control, themeColors?.primary],
  );
  // const handleSearchPreview = (value: any) => {
  //   setSearchText(value);
  //   if (!value) {
  //     getPreview();
  //   } else {
  //     const filtered = (report || []).filter((item) =>
  //       Object.values(item).some((val) =>
  //         String(val).toLowerCase().includes(value.toLowerCase())
  //       )
  //     );
  //     setReport(filtered);
  //   }
  // };
  const drawColumns = (obj: any, reportMeta?: { key?: string; value?: number; label?: string }) => {
    const keys = Object.keys(obj).filter((key) => key !== "paymentMethods");

    // Credit Note Report: Customer → CN # → CN date → Inv no → Inv date → amount
    const creditNoteColumnOrder = [
      "customer",
      "customerName",
      "creditNoteNumber",
      "creditNoteNo",
      "creditNoteDate",
      "invoiceNumber",
      "invoiceNo",
      "invoiceDate",
      "amount",
    ];
    const isCreditNoteReport =
      reportMeta?.key === "CREDIT_NOTE_REPORT" ||
      keys.includes("creditNoteNumber") ||
      keys.includes("creditNoteNo");

    const isInvoiceDetailReport =
      reportMeta?.key === "INVOICE_DETAIL_REPORT" ||
      reportMeta?.value === 14 ||
      reportMeta?.label === "Invoice Detail Report";

    const hiddenCols = isInvoiceDetailReport
      ? ["invoiceId", "clientId"]
      : [];

    const visibleKeys = keys.filter((key) => !hiddenCols.includes(key));

    const orderedKeys = isCreditNoteReport
      ? [
          ...creditNoteColumnOrder.filter((key) => visibleKeys.includes(key)),
          ...visibleKeys.filter((key) => !creditNoteColumnOrder.includes(key)),
        ]
      : visibleKeys;

    return orderedKeys.map((key) => ({
      label:
        t(key) ||
        key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase()),
      id: key,
    }));
  };
  const handleChangePage = (page: number) => {
    setPageSize(pageSize);
    getPreview(page + 1, pageSize);
  };
  const handleChangeRows = (size: number) => {
    setPageSize(size);
    getPreview(1, size);
  };
  const getPreview = (page: number, size: number) => {
    setReportPreviewLoader(true);
    const payload = {
      ...getValues(),
      reportNumber: getValues().reportNumber.value,
      clientId: getValues().clientId?.length
        ? getValues().clientId?.join(",")
        : null,
      month: getValues()?.month?.value || null,
      year: getValues()?.year?.value || null,
      ...(getValues().reportNumber.value === 202 ||
      getValues().reportNumber.value === 302
        ? {
            accountId: getValues().vehicleId?.length
              ? getValues().vehicleId?.join(",")
              : null,
          }
        : {
            vehicleId: getValues().vehicleId?.length
              ? getValues().vehicleId?.join(",")
              : null,
          }),
      ...(Array.isArray(getValues().stationId) &&
        getValues().stationId.length && {
          stationId: getValues()
            .stationId.map((item: any) => item.value)
            .join(","),
        }),
      page: page,
      size: size,
    };
    delete payload.reportType;
    delete payload.dateRange;
    if (
      getValues().reportNumber.value === 202 ||
      getValues().reportNumber.value === 302
    ) {
      payload?.vehicleId &&
        payload?.vehicleId?.length &&
        delete payload?.vehicleId;
    }
    ReportsProfileService.getReportPreview(payload)
      .then((res: GetReportPreviewResponse) => {
        if (res.success) {
          setReport(res?.data || {});
          setShowReportPreview(true);
          setReportPreviewLoader(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={t(res.message) || "Something went wrong"} />,
            { variant: "error" },
          );
          setReportPreviewLoader(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setReportPreviewLoader(false);
      });
  };
  const handleFormSubmit = (data: FormData) => {
    onSubmit(data, "excel");
  };
  const onSubmit = (data: FormData, type: "pdf" | "excel") => {
    if (Object.values(data)?.length) {
      if (type === "excel") setIsBtnEnabled(true);
      else setIsPdfBtnEnabled(true);
      const payload = {
        ...data,
        reportNumber: data.reportNumber.value,
        clientId: data.clientId?.length ? data.clientId : null,
        month: data?.month?.value || null,
        year: data?.year?.value || null,
        ...(data.reportNumber.value === 202 || data.reportNumber.value === 302
          ? { accountId: data.vehicleId?.length ? data.vehicleId : null }
          : { vehicleId: data.vehicleId?.length ? data.vehicleId : null }),
        ...(Array.isArray(data.stationId) &&
          data.stationId.length && {
            stationId: data.stationId.map((item: any) => item.value),
          }),
      };
      delete (payload as any).reportType;
      delete (payload as any).dateRange;
      if (
        getValues().reportNumber.value === 202 ||
        getValues().reportNumber.value === 302
      )
        delete (payload as any).vehicleId;
      ReportsProfileService.downloadReport(
        payload,
        type === "excel" ? "reports/export" : "reports/export/pdf",
      )
        .then((response: Blob) => {
          const blob = new Blob([response], {
            type:
              type === "excel"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf",
          });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            type === "excel"
              ? `${data.reportNumber.label}.xlsx`
              : `${data.reportNumber.label}.pdf`,
          );
          document.body.appendChild(link);
          link.click();

          link.remove();
          window.URL.revokeObjectURL(url);
          enqueueSnackbar(
            <AlertMessages
              text={
                t("report") + " " + t("downloadedSuccessfully")
              }
            />,
            { variant: "success" },
          );
          // reset();
          // setCurrentStep(0);
          if (type === "excel") setIsBtnEnabled(false);
          else setIsPdfBtnEnabled(false);
          // setShowReportPreview(false);
          // setReport([]);
          // setSearchText("");
        })
        .catch((err: any) => {
          if (type === "excel") setIsBtnEnabled(false);
          else setIsPdfBtnEnabled(false);
          console.error("===>>>", err);
          enqueueSnackbar(<AlertMessages statusCode={err?.status} />, {
            variant: "error",
          });
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stations")
        stationsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "customers")
        fetchCustomers(
          action === "loadNext" ? dropdownPage + 1 : 1,
          search || "",
          action,
        );
      else if (typeName === "vehicles")
        fetchVehicles(
          action === "loadNext" ? dropdownPage + 1 : 1,
          search || "",
          action,
        );
    }, 300);
  };
  return (
    <Grid
      container
      component="form"
      id="form"
      className="px-[8px] reports"
      spacing={2}
      onSubmit={handleSubmit(handleFormSubmit)}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "Enter") event.preventDefault();
      }}
    >
      <Grid item xs={12}>
        <ComponentHeaderTwo
          showBtn={false}
          showSaveBtn={false}
          heading={t("reports")}
          icon={
            <AssessmentOutlinedIcon
              sx={{
                fill: "#000",
                width: "30px",
                height: "30px",
                marginTop: "3px",
              }}
            />
          }
          showCreateBtn={false}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <Grid
          item
          xs={12}
          sx={{
            "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer":
              {
                background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
              },
          }}
        >
          <Stepper
            className={`reportSteps bg-[#fff] rounded-[8px] ps-[15px] pe-[20px] ${currentStep === steps.length - 1 ? "py-[15px]" : "pt-[15px]"}`}
            activeStep={currentStep}
            orientation="vertical"
            connector={null}
          >
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: { optional?: React.ReactNode } = {};
              return (
                <Step key={index} {...stepProps} className="relative z-1">
                  <StepLabel
                    {...labelProps}
                    className={`${index === 2 && (watch("reportType")?.value === 2 || watch("reportNumber")?.value === 203 || watch("reportNumber")?.value === 204 || watch("reportNumber")?.value === 303 || watch("reportNumber")?.value === 205 || watch("reportNumber")?.value === 12 || watch("reportNumber")?.value === 206) ? "opacity-25" : "opacity-100"}`}
                    style={{
                      filter:
                        index === 3 &&
                        (watch("reportNumber")?.value === 101 ||
                          watch("reportNumber")?.value === 109 ||
                          watch("reportNumber")?.value === 111 ||
                          watch("reportNumber")?.value === 112 ||
                          watch("reportNumber")?.value === 113)
                          ? "opacity(0.25)"
                          : "opacity(1)",
                      cursor: index < currentStep ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      const target = e.currentTarget as HTMLElement;
                      if (
                        index < currentStep &&
                        target.classList.contains("opacity-100")
                      )
                        setCurrentStep(index);
                    }}
                  >
                    {t(label)}
                  </StepLabel>
                  {index !== steps.length - 1 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      className="bg-[#B8B8B8] absolute left-[11px] top-[24px] h-[calc(100%-24px)] w-[1px] z-0"
                    />
                  )}
                  <Box>
                    {currentStep === 0 && currentStep === index ? (
                      <>
                        {/* <Controller
                        name="reportType"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <Box className="ps-[32px]">
                            <DynamicSelect
                              label={t("select") + " " + t("reportType") + " *"}
                              {...field}
                              options={[
                                { value: 1, label: t("sales") },
                                { value: 2, label: t("Inventory") },
                                { value: 3, label: t("kpi") },
                                { value: 4, label: t("others") },
                              ]}
                              isSearchable
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                trigger("reportType");
                                reset({ reportType: getValues("reportType") });
                              }}
                              error={errors.reportType?.message as string}
                              value={{
                                label: t(watch("reportType")?.label),
                                value: watch("reportType")?.value,
                              }}
                            />
                          </Box>
                        )}
                      /> */}
                        <Controller
                          name="reportType"
                          control={control}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <Box className="ps-[32px]">
                              <label className="text-left block mb-2">
                                {t("select") + " " + t("reportType") + " *"}
                              </label>
                              <RadioGroup
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                {...field}
                                value={field.value?.value || ""}
                                onChange={(e: any) => {
                                  const selectedOption = {
                                    value: Number(e.target.value),
                                    label:
                                      e.target.value === "1"
                                        ? t("sales")
                                        : e.target.value === "2"
                                          ? t("Inventory")
                                          : e.target.value === "3"
                                            ? t("kpi")
                                            : t("others"),
                                  };
                                  field.onChange(selectedOption);
                                  trigger("reportType");
                                  reset({
                                    reportType: getValues("reportType"),
                                  });
                                }}
                                sx={{
                                  "& .MuiRadio-root": { display: "none" },
                                  "& .MuiFormControlLabel-root": {
                                    background: themeColors?.appBg,
                                    padding: "8px",
                                    borderRadius: "8px",
                                    marginRight: "0px !important",
                                    marginBottom: "5px !important",
                                    marginLeft: "0px",
                                  },
                                  "& .MuiFormControlLabel-root:has(.Mui-checked)":
                                    {
                                      border: "1px solid",
                                      borderColor: themeColors.primary,
                                    },
                                  "& .MuiFormControlLabel-root:hover": {
                                    outline: "1px solid",
                                    outlineColor: themeColors.primary,
                                  },
                                  "& .MuiFormControlLabel-root svg path": {
                                    fill: "#000",
                                  },
                                  "& .MuiFormControlLabel-root:has(.Mui-checked) svg path":
                                    { fill: themeColors?.primary },
                                  "& .MuiFormControlLabel-root:has(.Mui-checked) .MuiTypography-root span":
                                    { color: themeColors?.primary },
                                }}
                              >
                                {checkSubscriptions("SALES_REPORTS") && (
                                  <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label={
                                      <span className="flex gap-2 items-center">
                                        <SalesReportIcon />
                                        {t("sales")}
                                      </span>
                                    }
                                  />
                                )}
                                {checkSubscriptions("INVENTORY_REPORTS") && (
                                  <FormControlLabel
                                    value={2}
                                    control={<Radio />}
                                    label={
                                      <span className="flex gap-2 items-center">
                                        <InventoryReportIcon /> {t("Inventory")}
                                      </span>
                                    }
                                  />
                                )}
                                {checkSubscriptions("KPI_REPORTS") && (
                                  <FormControlLabel
                                    value={3}
                                    control={<Radio />}
                                    label={
                                      <span className="flex gap-2 items-center">
                                        <KpiReportIcon />
                                        {t("kpi")}
                                      </span>
                                    }
                                  />
                                )}
                                {checkSubscriptions("OTHERS_REPORTS") && (
                                  <FormControlLabel
                                    value={4}
                                    control={<Radio />}
                                    label={
                                      <span className="flex gap-2 items-center">
                                        <OthersReportIcon />
                                        {t("others")}
                                      </span>
                                    }
                                  />
                                )}
                              </RadioGroup>
                              {errors.reportType?.message && (
                                <Typography
                                  color="error"
                                  variant="caption"
                                  className="font-Saira text-[11px] leading-[18px] font-medium italic text-left block text-[#EF5350] absolute"
                                >
                                  {errors.reportType?.message as string}
                                </Typography>
                              )}
                            </Box>
                          )}
                        />
                      </>
                    ) : null}
                    {currentStep === 1 && currentStep === index && (
                      <>
                        <Controller
                          name="reportNumber"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <Box className="ps-[32px]">
                              <DynamicSelect
                                label={t("selectReportTemplate") + " *"}
                                {...field}
                                options={reportOptions?.map((report) => ({
                                  ...report,
                                  label: t(report.label),
                                }))}
                                isSearchable
                                isLoading={loading}
                                defaultPlaceholder="phSelectReportTemplate"
                                onChange={(selectedOption: any) => {
                                  field.onChange(selectedOption);
                                  trigger("reportNumber");
                                  // resetField("stationId");
                                  if (
                                    watch("reportNumber")?.value === 203 ||
                                    watch("reportNumber")?.value === 204 ||
                                    watch("reportNumber")?.value === 303 ||
                                    watch("reportNumber")?.value === 205 ||
                                    watch("reportNumber")?.value === 12 ||
                                    watch("reportNumber")?.value === 206
                                  ) {
                                    resetField("clientId");
                                    resetField("vehicleId");
                                  }
                                  if (
                                    watch("reportNumber")?.value === 202 ||
                                    watch("reportNumber")?.value === 302
                                  )
                                    resetField("vehicleId");
                                  else resetField("accountId");
                                  if (
                                    watch("reportNumber")?.value === 4 ||
                                    watch("reportNumber")?.value === 201 ||
                                    watch("reportNumber")?.value === 12 ||
                                    watch("reportNumber")?.value === 13 ||
                                    watch("reportNumber")?.value === 16 ||
                                    watch("reportNumber")?.value === 17
                                  ) {
                                    setTab(0);
                                    resetField("vehicleId");
                                  }
                                  if (
                                    watch("reportNumber")?.value === 101 ||
                                    watch("reportNumber")?.value === 109 ||
                                    watch("reportNumber")?.value === 111 ||
                                    watch("reportNumber")?.value === 112 ||
                                    watch("reportNumber")?.value === 113 ||
                                    watch("reportNumber")?.value === 205
                                  ) {
                                    resetField("start");
                                    resetField("end");
                                    resetField("dateRange");
                                  }
                                  if (
                                    showReportPreview &&
                                    (watch("reportNumber")?.value !== 101 ||
                                      watch("reportNumber")?.value !== 109 ||
                                      watch("reportNumber")?.value !== 111 ||
                                      watch("reportNumber")?.value !== 112 ||
                                      watch("reportNumber")?.value !== 113)
                                  ) {
                                    setShowReportPreview(false);
                                    setReport([]);
                                  }
                                  if (
                                    watch("reportNumber")?.value === 205 ||
                                    watch("reportNumber")?.value === 105
                                  )
                                    setValue("stationId", null);
                                  else {
                                    resetField("month");
                                    resetField("year");
                                  }
                                }}
                                error={errors.reportNumber?.message as string}
                                value={{
                                  label: watch("reportNumber")?.label,
                                  value: watch("reportNumber")?.value,
                                }}
                              />
                            </Box>
                          )}
                        />
                        {watch("reportNumber")?.value === 205 ||
                        watch("reportNumber")?.value === 105 ? (
                          <></>
                        ) : (
                          <Controller
                            name="stationId"
                            control={control}
                            defaultValue={null}
                            rules={{
                              required:
                                watch("reportNumber")?.value === 4 ||
                                watch("reportNumber")?.value === 201 ||
                                watch("reportNumber")?.value === 204 ||
                                watch("reportNumber")?.value === 108 ||
                                watch("reportNumber")?.value === 109 ||
                                watch("reportNumber")?.value === 110 ||
                                watch("reportNumber")?.value === 111 ||
                                watch("reportNumber")?.value === 112 ||
                                watch("reportNumber")?.value === 113 ||
                                watch("reportNumber")?.value === 206
                                  ? t("This field is required.")
                                  : false,
                            }}
                            render={({ field }) => (
                              <Box className="ps-[32px]">
                                <DynamicSelect
                                  isMulti
                                  label={
                                    t("station") +
                                    (watch("reportNumber")?.value === 4 ||
                                    watch("reportNumber")?.value === 201 ||
                                    watch("reportNumber")?.value === 204 ||
                                    watch("reportNumber")?.value === 108 ||
                                    watch("reportNumber")?.value === 109 ||
                                    watch("reportNumber")?.value === 110 ||
                                    watch("reportNumber")?.value === 111 ||
                                    watch("reportNumber")?.value === 112 ||
                                    watch("reportNumber")?.value === 113 ||
                                    watch("reportNumber")?.value === 206
                                      ? " *"
                                      : "")
                                  }
                                  {...field}
                                  options={stations}
                                  isSearchable
                                  isClearable
                                  isLoading={
                                    loading || searchLoading === "stations"
                                  }
                                  onChange={(selectedOption: any) => {
                                    field.onChange(selectedOption);
                                    trigger("stationId");
                                  }}
                                  error={errors.stationId?.message as string}
                                  defaultPlaceholder="phSelectStation"
                                  onInputChange={(
                                    search: string,
                                    { action }: { action: string },
                                  ) => {
                                    if (
                                      action === "input-change" ||
                                      (action !== "set-value" &&
                                        action !== "menu-close" &&
                                        action !== "input-blur")
                                    )
                                      handleDropdownActions(
                                        "stations",
                                        "searchIt",
                                        search,
                                      );
                                    else if (
                                      action === "input-blur" ||
                                      action === "menu-close"
                                    ) {
                                      if (
                                        dropdownSearchTerm ||
                                        dropdownPage > 1
                                      )
                                        handleDropdownActions(
                                          "stations",
                                          "searchIt",
                                          "",
                                        );
                                      else setDropdownSearchTerm("");
                                      setDropdownPage(1);
                                    }
                                  }}
                                  onMenuScrollToBottom={() => {
                                    if (
                                      !(stationsData?.pages[0]?.data as any)
                                        .last
                                    )
                                      handleDropdownActions(
                                        "stations",
                                        "loadNext",
                                      );
                                  }}
                                  page={dropdownPage}
                                />
                              </Box>
                            )}
                          />
                        )}
                      </>
                    )}
                    {currentStep === 2 && currentStep === index ? (
                      <Box className="ps-[32px]">
                        <Box
                          className="flex justify-between items-center"
                          sx={{
                            "& .MuiTabs-root .MuiTab-root.Mui-selected": {
                              background: themeColors?.primary,
                              color: "#fff !important",
                            },
                          }}
                        >
                          <Typography className="font-Saira text-[13px] text-[#343434] font-semibold mt-[-12px]">
                            {t("select")}
                          </Typography>
                          <Tabs
                            value={tab}
                            aria-label="tabs"
                            onChange={(_, value: number) => {
                              if (dropdownSearchTerm || dropdownPage > 1) {
                                if (tab === 0)
                                  handleDropdownActions(
                                    "customers",
                                    "searchIt",
                                    "",
                                  );
                                else
                                  handleDropdownActions(
                                    "vehicles",
                                    "searchIt",
                                    "",
                                  );
                              }
                              setTimeout(() => setTab(value), 0);
                            }}
                          >
                            <Tab
                              label={t("customers")}
                              id="tab-0"
                              aria-controls="tabpanel-0"
                              style={{
                                borderColor: themeColors?.primary,
                                color: themeColors?.primary,
                              }}
                            />
                            {watch("reportNumber")?.value === 4 ||
                            watch("reportNumber")?.value === 201 ||
                            watch("reportNumber")?.value === 12 ||
                            watch("reportNumber")?.value === 13 ||
                            watch("reportNumber")?.value === 16 ||
                            watch("reportNumber")?.value === 17 ? null : (
                              <Tab
                                label={
                                  watch("reportNumber")?.value === 202 ||
                                  watch("reportNumber")?.value === 302
                                    ? t("companies")
                                    : t("vehicles")
                                }
                                id="tab-1"
                                aria-controls="tabpanel-1"
                                className="ml-[10px]"
                                style={{
                                  borderColor: themeColors?.primary,
                                  color: themeColors?.primary,
                                }}
                              />
                            )}
                          </Tabs>
                        </Box>
                        {tab === 0 && (
                          <Box
                            role="tabpanel"
                            className="report-tab"
                            id="simple-tabpanel-0"
                            aria-labelledby={`simple-tab-0`}
                          >
                            <StyledInput
                              fullWidth
                              placeholder={t("phSearchCustomers")}
                              hideBtn
                              handleChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleDropdownActions(
                                  "customers",
                                  "searchIt",
                                  event.target.value?.trim(),
                                )
                              }
                              value={dropdownSearchTerm}
                            />
                            {tab === 0 ? (
                              <Box
                                className="reportUnits mt-4"
                                onScroll={(event: any) => {
                                  const target = event.currentTarget;
                                  if (
                                    target.scrollTop + target.clientHeight >=
                                      target.scrollHeight &&
                                    !filteredCustomers?.last &&
                                    !customerLoader
                                  ) {
                                    handleDropdownActions(
                                      "customers",
                                      "loadNext",
                                    );
                                  }
                                }}
                              >
                                {filteredCustomers?.content?.length ? (
                                  customerCheckboxes
                                ) : (
                                  <Typography
                                    className="font-Saira italic font-[500] text-[13px]"
                                    sx={{ color: themeColors?.primary }}
                                  >
                                    {customerLoader ? "" : t("noRecordFound")}
                                  </Typography>
                                )}
                              </Box>
                            ) : null}
                            {customerLoader ? (
                              <CircularProgress
                                sx={{ color: themeColors?.primary }}
                                size={30}
                                className="absolute bottom-[70px]"
                              />
                            ) : null}
                            <Typography className="text-[#FF0000] font-[500] text-[11px] font-Saira italic text-left ml-[10px] absolute">
                              {t(errors.clientId?.message as string)}
                            </Typography>
                          </Box>
                        )}
                        {tab === 1 && (
                          <Box
                            role="tabpanel"
                            className="report-tab"
                            id="simple-tabpanel-1"
                            aria-labelledby={`simple-tab-1`}
                          >
                            <StyledInput
                              fullWidth
                              placeholder={
                                watch("reportNumber")?.value === 202 ||
                                watch("reportNumber")?.value === 302
                                  ? t("phSearchCompanies")
                                  : t("phSearchByMakeModelPlate")
                              }
                              hideBtn
                              handleChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleDropdownActions(
                                  "vehicles",
                                  "searchIt",
                                  event.target.value?.trim(),
                                )
                              }
                              value={dropdownSearchTerm}
                            />
                            {tab === 1 ? (
                              <Box
                                className="reportUnits mt-4"
                                onScroll={(event: any) => {
                                  const target = event.currentTarget;
                                  if (
                                    target.scrollTop + target.clientHeight >=
                                      target.scrollHeight &&
                                    !filteredVehicles?.last &&
                                    !vehicleLoader
                                  ) {
                                    handleDropdownActions(
                                      "vehicles",
                                      "loadNext",
                                    );
                                  }
                                }}
                              >
                                {filteredVehicles?.content?.length ? (
                                  vehicleCheckboxes
                                ) : (
                                  <Typography
                                    className="font-Saira italic font-[500] text-[13px]"
                                    sx={{ color: themeColors?.primary }}
                                  >
                                    {vehicleLoader ? "" : t("noRecordFound")}
                                  </Typography>
                                )}
                              </Box>
                            ) : null}
                            {vehicleLoader ? (
                              <CircularProgress
                                sx={{ color: themeColors?.primary }}
                                size={30}
                                className="absolute bottom-[70px]"
                              />
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    ) : null}
                    {currentStep === 3 && currentStep === index ? (
                      <>
                        {watch("reportNumber")?.value !== 205 ? (
                          <Box className="ps-[32px]">
                            <Controller
                              name="dateRange"
                              control={control}
                              rules={{
                                required: t("This field is required."),
                                validate: (value) => {
                                  if (!value || !value[0] || !value[1]) {
                                    return t("startEndDatesRequired");
                                  }
                                  if (
                                    !dayjs(value[0]).isValid() ||
                                    !dayjs(value[1]).isValid()
                                  ) {
                                    return t("Invalid Date");
                                  }
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <DateRangePicker
                                  {...field}
                                  label={t("select") + " " + t("dateInterval")}
                                  maxDate={dayjs()}
                                  placeholder={t("phDateRangeFormat")}
                                  onChange={(range: any) => {
                                    field.onChange(range);
                                    trigger("dateRange");
                                    setValue(
                                      "start",
                                      range[0]?.format("YYYY-MM-DD"),
                                    );
                                    setValue(
                                      "end",
                                      range[1]?.format("YYYY-MM-DD"),
                                    );
                                  }}
                                  shortcuts={[
                                    {
                                      label: t("today"),
                                      getValue: () => [dayjs(), dayjs()],
                                    },
                                    {
                                      label: t("yesterday"),
                                      getValue: () => [
                                        dayjs().subtract(1, "day"),
                                        dayjs().subtract(1, "day"),
                                      ],
                                    },
                                    {
                                      label: t("thisWeek"),
                                      getValue: () => [
                                        dayjs().startOf("week"),
                                        dayjs(),
                                      ],
                                    },
                                    {
                                      label: t("lastWeek"),
                                      getValue: () => [
                                        dayjs()
                                          .subtract(1, "week")
                                          .startOf("week"),
                                        dayjs()
                                          .subtract(1, "week")
                                          .endOf("week"),
                                      ],
                                    },
                                    {
                                      label: t("thisMonth"),
                                      getValue: () => [
                                        dayjs().startOf("month"),
                                        dayjs(),
                                      ],
                                    },
                                    {
                                      label: t("lastMonth"),
                                      getValue: () => [
                                        dayjs()
                                          .subtract(1, "month")
                                          .startOf("month"),
                                        dayjs()
                                          .subtract(1, "month")
                                          .endOf("month"),
                                      ],
                                    },
                                    {
                                      label: t("thisYear"),
                                      getValue: () => [
                                        dayjs().startOf("year"),
                                        dayjs(),
                                      ],
                                    },
                                    {
                                      label: t("lastYear"),
                                      getValue: () => [
                                        dayjs()
                                          .subtract(1, "year")
                                          .startOf("year"),
                                        dayjs()
                                          .subtract(1, "year")
                                          .endOf("year"),
                                      ],
                                    },
                                    {
                                      label: t("customDateRange"),
                                      getValue: () => [null, null],
                                    },
                                  ]}
                                  error={errors.dateRange?.message as string}
                                />
                              )}
                            />
                          </Box>
                        ) : (
                          <Box className="ps-[32px]">
                            <Controller
                              name="month"
                              control={control}
                              defaultValue={null}
                              rules={{ required: t("This field is required.") }}
                              render={({ field }) => (
                                <DynamicSelect
                                  label={t("month") + " *"}
                                  {...field}
                                  options={[
                                    { value: 1, label: t("january") },
                                    { value: 2, label: t("february") },
                                    { value: 3, label: t("march") },
                                    { value: 4, label: t("april") },
                                    { value: 5, label: t("may") },
                                    { value: 6, label: t("june") },
                                    { value: 7, label: t("july") },
                                    { value: 8, label: t("august") },
                                    { value: 9, label: t("september") },
                                    { value: 10, label: t("october") },
                                    { value: 11, label: t("november") },
                                    { value: 12, label: t("december") },
                                  ]}
                                  isSearchable
                                  defaultPlaceholder="phSelectMonth"
                                  onChange={(selectedOption: any) =>
                                    field.onChange(selectedOption)
                                  }
                                  error={errors.month?.message as string}
                                />
                              )}
                            />
                            <Controller
                              name="year"
                              control={control}
                              defaultValue={null}
                              rules={{ required: t("This field is required.") }}
                              render={({ field }) => (
                                <DynamicSelect
                                  label={t("year") + " *"}
                                  {...field}
                                  options={Array.from(
                                    { length: 100 },
                                    (_, i) => ({
                                      label: new Date().getFullYear() - i,
                                      value: new Date().getFullYear() - i,
                                    }),
                                  )}
                                  isSearchable
                                  defaultPlaceholder="phSelectYear"
                                  onChange={(selectedOption: any) =>
                                    field.onChange(selectedOption)
                                  }
                                  error={errors.year?.message as string}
                                />
                              )}
                            />
                          </Box>
                        )}
                      </>
                    ) : null}
                    <Box
                      className={`flex mt-5 ${currentStep === 0 ? "justify-end" : "justify-between"} gap-1 ${currentStep === steps.length - 1 ? "" : "ps-[32px]"}`}
                    >
                      {currentStep !== 0 && currentStep === index ? (
                        <Button
                          className="secondary"
                          style={{
                            color: themeColors?.primary,
                            padding: "10px 18px",
                          }}
                          variant="contained"
                          disableElevation
                          onClick={() => {
                            setCurrentStep(
                              (watch("reportType")?.value === 2 ||
                                watch("reportNumber")?.value === 203 ||
                                watch("reportNumber")?.value === 204 ||
                                watch("reportNumber")?.value === 303 ||
                                watch("reportNumber")?.value === 205 ||
                                watch("reportNumber")?.value === 12 ||
                                watch("reportNumber")?.value === 206) &&
                                currentStep === 3
                                ? currentStep - 2
                                : currentStep - 1,
                            );
                            if (
                              (watch("reportNumber")?.value === 101 ||
                                watch("reportNumber")?.value === 109 ||
                                watch("reportNumber")?.value === 111 ||
                                watch("reportNumber")?.value === 112 ||
                                watch("reportNumber")?.value === 113) &&
                              currentStep === 1
                            )
                              resetField("reportNumber");
                          }}
                        >
                          {t("back")}
                        </Button>
                      ) : null}
                      {currentStep !== steps.length - 1 &&
                      currentStep === index &&
                      watch("reportNumber")?.value !== 101 &&
                      watch("reportNumber")?.value !== 109 &&
                      watch("reportNumber")?.value !== 111 &&
                      watch("reportNumber")?.value !== 112 &&
                      watch("reportNumber")?.value !== 113 ? (
                        <Button
                          className="primary"
                          style={{
                            background: themeColors?.primary,
                            padding: "10px 18px",
                          }}
                          variant="contained"
                          disableElevation
                          onClick={async () => {
                            const isValid = await trigger();
                            if (isValid) {
                              setCurrentStep(
                                (watch("reportType")?.value === 2 ||
                                  watch("reportNumber")?.value === 203 ||
                                  watch("reportNumber")?.value === 204 ||
                                  watch("reportNumber")?.value === 303 ||
                                  watch("reportNumber")?.value === 205 ||
                                  watch("reportNumber")?.value === 12 ||
                                  watch("reportNumber")?.value === 206) &&
                                  currentStep === 1
                                  ? currentStep + 2
                                  : currentStep + 1,
                              );
                              setLoading(true);
                            }
                          }}
                        >
                          {t("Next")}
                        </Button>
                      ) : null}
                      {(currentStep === steps.length - 1 &&
                        currentStep === index) ||
                      (currentStep === 1 &&
                        index === 1 &&
                        (watch("reportNumber")?.value === 101 ||
                          watch("reportNumber")?.value === 109 ||
                          watch("reportNumber")?.value === 111 ||
                          watch("reportNumber")?.value === 112 ||
                          watch("reportNumber")?.value === 113)) ? (
                        <Box className="flex gap-1">
                          <Button
                            className="primary"
                            style={{
                              background: themeColors?.primary,
                              padding: "10px 18px",
                            }}
                            variant="contained"
                            disableElevation
                            onClick={async () => {
                              const isValid = await trigger();
                              if (isValid) {
                                setShowReportPreview(true);
                                setReport([]);
                                setPageSize(10);
                                getPreview(1, 10);
                              }
                            }}
                          >
                            {t("submit")}
                          </Button>
                          <Tooltip enterDelay={0} title={t("downloadPDF")}>
                            <Button
                              type="submit"
                              disabled={IsPdfBtnEnabled}
                              className="primary"
                              style={{
                                background: themeColors?.primary,
                                padding: "8px",
                              }}
                              variant="contained"
                              disableElevation
                              onClick={handleSubmit((data) =>
                                onSubmit(data, "pdf"),
                              )}
                            >
                              {IsPdfBtnEnabled ? (
                                <CircularProgress
                                  className="text-white"
                                  size={18}
                                />
                              ) : (
                                <DownloadPDF className="w-[18px] h-[18px] max-w-[18px]" />
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip enterDelay={0} title={t("downloadExcel")}>
                            <Button
                              type="submit"
                              disabled={IsBtnEnabled}
                              className="primary downloadbtn"
                              style={{ background: themeColors?.primary }}
                              variant="contained"
                              disableElevation
                              onClick={handleSubmit((data) =>
                                onSubmit(data, "excel"),
                              )}
                            >
                              {IsBtnEnabled ? (
                                <CircularProgress
                                  className="text-white"
                                  size={24}
                                />
                              ) : (
                                <FileDownloadOutlinedIcon />
                              )}
                            </Button>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </Box>
                    {index !== steps.length - 1 && (
                      <Divider
                        className={`bg-[#D2BFD5] ms-[32px] ${currentStep === index ? "mt-[17px] mb-[10px]" : "mt-[-10px]"}`}
                      />
                    )}
                  </Box>
                </Step>
              );
            })}
          </Stepper>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={8}>
        {showReportPreview ? (
          reportPreviewLoader ? (
            <Backdrop sx={{ background: "rgba(0, 0, 0, 0.1)" }} open={true}>
              <CircularProgress
                sx={{ color: themeColors?.primary }}
                size={35}
              />
            </Backdrop>
          ) : (
            <>
              <Box className="flex justify-between items-center">
                <Typography className="font-Saira text-[17px] text-[#CDC4D4] uppercase font-semibold">
                  {t(watch("reportNumber")?.label) || t("noReportSelected")}
                </Typography>
                {/* <StyledInput
                  fullWidth
                  placeholder={t("search")}
                  hideBtn
                  handleChange={(event: any) =>
                    handleSearchPreview(event.target.value)
                  }
                  value={searchText}
                /> */}
              </Box>
              <ReportPreviewTable
                rows={report?.content || []}
                columns={drawColumns(
                  report?.content?.[0] || {},
                  watch("reportNumber"),
                )}
                reportNumber={watch("reportNumber")?.value}
                tableData={report || {}}
                handleChangePage={handleChangePage}
                handleChangeRows={handleChangeRows}
                {...{ searchText, pageSize }}
              />
            </>
          )
        ) : (
          <Box className="flex flex-col justify-center h-[100%]">
            <Typography className="font-Saira text-[17px] text-[#CDC4D4] capitalize font-semibold">
              {t(watch("reportNumber")?.label) || t("noReportSelected")}
            </Typography>
            <EmptyReport className="d-block mx-auto" />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
export default Reports;
